import { useSyncExternalStore } from 'react';

/**
 * PWA-Laufzeit: Service-Worker-Registrierung, Update-Erkennung und Installationsaufforderung.
 *
 * Im Entwicklungsmodus (Vite-HMR) wird standardmäßig KEIN Service Worker registriert und vorhandene
 * Registrierungen werden abgemeldet. Zum Testen im Dev-Modus: VITE_PWA_DEV=true in apps/web/.env.local
 * oder im Browser localStorage.setItem('mv:pwa-dev', '1') und neu laden.
 */

/** Chromium-Ereignis „beforeinstallprompt“ (nicht in lib.dom enthalten) */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

interface PwaState {
  /** Ein neuer Service Worker ist installiert und wartet auf Aktivierung */
  updateAvailable: boolean;
  /** Installationsaufforderung (nur Chromium-Browser) */
  installPrompt: BeforeInstallPromptEvent | null;
  /** App läuft bereits als installierte App */
  standalone: boolean;
  /** Service Worker ist registriert */
  swActive: boolean;
}

let state: PwaState = {
  updateAvailable: false,
  installPrompt: null,
  standalone: detectStandalone(),
  swActive: false,
};
const listeners = new Set<() => void>();
let waitingWorker: ServiceWorker | null = null;

function setState(patch: Partial<PwaState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

export function usePwa(): PwaState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
  );
}

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function isIos(): boolean {
  const ua = navigator.userAgent;
  // iPadOS meldet sich als „Macintosh“ mit Touch
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

function safeStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function devSwEnabled(): boolean {
  return import.meta.env.VITE_PWA_DEV === 'true' || safeStorageGet('mv:pwa-dev') === '1';
}

/** Installationsaufforderung auslösen (Chromium). Liefert true, wenn der Benutzer installiert hat. */
export async function promptInstall(): Promise<boolean> {
  const p = state.installPrompt;
  if (!p) return false;
  await p.prompt();
  const choice = await p.userChoice.catch(() => ({ outcome: 'dismissed' as const }));
  setState({ installPrompt: null });
  return choice.outcome === 'accepted';
}

/** Wartenden Service Worker aktivieren und Seite neu laden. */
export function applyUpdate(): void {
  if (!waitingWorker) {
    window.location.reload();
    return;
  }
  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  });
  waitingWorker.postMessage({ type: 'SKIP_WAITING' });
}

function trackWaiting(reg: ServiceWorkerRegistration) {
  const markWaiting = (w: ServiceWorker | null) => {
    // Nur ein UPDATE melden (beim allerersten Install gibt es noch keinen Controller)
    if (w && navigator.serviceWorker.controller) {
      waitingWorker = w;
      setState({ updateAvailable: true });
    }
  };
  markWaiting(reg.waiting);
  reg.addEventListener('updatefound', () => {
    const installing = reg.installing;
    if (!installing) return;
    installing.addEventListener('statechange', () => {
      if (installing.state === 'installed') markWaiting(reg.waiting ?? installing);
    });
  });
}

/** Einmalig beim App-Start aufrufen (main.tsx). */
export function initPwa(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // eigenen, dezenten Hinweis statt Browser-Mini-Infobar anzeigen
    setState({ installPrompt: e as BeforeInstallPromptEvent });
  });
  window.addEventListener('appinstalled', () => setState({ installPrompt: null, standalone: true }));
  window.matchMedia?.('(display-mode: standalone)').addEventListener?.('change', () => setState({ standalone: detectStandalone() }));

  if (!('serviceWorker' in navigator)) return;

  if (import.meta.env.DEV && !devSwEnabled()) {
    // Vite-HMR darf nicht durch einen (alten) Service Worker gestört werden
    void navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => void r.unregister()));
    return;
  }

  const register = async () => {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' });
      setState({ swActive: true });
      trackWaiting(reg);
      // Regelmäßig und beim Zurückkehren in die App nach Updates suchen
      const check = () => void reg.update().catch(() => undefined);
      setInterval(check, 60 * 60 * 1000);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
    } catch {
      setState({ swActive: false });
    }
  };
  if (document.readyState === 'complete') void register();
  else window.addEventListener('load', () => void register(), { once: true });
}
